import { useState, useEffect, useMemo } from 'react';
import { 
  LayoutGrid, 
  BarChart3, 
  AlertTriangle, 
  Search, 
  Filter, 
  CheckCircle2, 
  Clock, 
  Users, 
  XCircle,
  HelpCircle,
  AlertOctagon,
  ArrowRight,
  Loader2,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { ScrollArea } from './ui/scroll-area';
import { ResponsiveContainer, Treemap, Tooltip as RechartsTooltip, LineChart, Line, XAxis, YAxis, CartesianGrid, Legend, AreaChart, Area } from 'recharts';
import { projectId } from '../utils/supabase/info';
import { visibleTrend, trendDomain, recordedTrendDays, formatTrendTick, formatTrendLabel, type TrendRange } from '../utils/portfolioTrend';

interface Engagement {
  companyId: string;
  companyName: string;
  totalItems: number;
  completedItems: number;
  waitingForConsultant: number;
  waitingForClient: number;
  blockedItems: number;
  progressPercentage: number;
  healthStatus: 'healthy' | 'at-risk' | 'needs-attention';
  lastActivity: string | null;
}

interface PortfolioHealthViewsProps {
  engagements: Engagement[];
  onViewClientDetails: (companyId: string) => void;
  accessToken: string;
}

type ViewType = 'grid' | 'executive' | 'risk';

// Mock domains if we can't extract them perfectly yet
const DOMAINS = [
  'Global',
  'Services / SRM',
  'Incident',
  'Change',
  'Problem',
  'PIR',
  'Dashboards',
  'Queues'
];

interface GridItemStatus {
  domain: string;
  status: 'complete' | 'in-progress' | 'needs-client' | 'needs-consultant' | 'blocked' | 'stale' | 'none';
  blockedBy?: string;
  lastUpdated?: string;
}

interface ClientGridData {
  companyId: string;
  statuses: Record<string, GridItemStatus>; // key is domain
  /** Names of the client's blocked config items, for the Risk Focus blockers list. */
  blockedNames: string[];
}

/** One color per cell state, from the theme tokens; the legend under the grid uses the same table. */
const CELL_STYLES: Record<GridItemStatus['status'], { color: string; label: string }> = {
  complete: { color: 'bg-success', label: 'Complete' },
  'in-progress': { color: 'bg-primary', label: 'In progress' },
  'needs-client': { color: 'bg-warning', label: 'Waiting on client' },
  'needs-consultant': { color: 'bg-pk-teal', label: 'Waiting on consultant' },
  blocked: { color: 'bg-destructive', label: 'Blocked' },
  stale: { color: 'bg-warning/60', label: 'Stale' },
  none: { color: 'bg-muted', label: 'No data yet' },
};

export function PortfolioHealthViews({ engagements, onViewClientDetails, accessToken }: PortfolioHealthViewsProps) {
  // State for view selection (persist in localStorage)
  const [currentView, setCurrentView] = useState<ViewType>(() => {
    return (localStorage.getItem('portfolioViewMode') as ViewType) || 'grid';
  });

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [showBlockedOnly, setShowBlockedOnly] = useState(false);
  const [showStale, setShowStale] = useState(false);
  const [showClientStalled, setShowClientStalled] = useState(false);
  const [showHighRisk, setShowHighRisk] = useState(false);

  // Data for Grid View
  const [gridData, setGridData] = useState<Record<string, ClientGridData>>({});
  const [loadingGrid, setLoadingGrid] = useState(false);

  // Trend Data & Live Sync State
  const [trendRange, setTrendRange] = useState<TrendRange>('90d');
  const [chartData, setChartData] = useState<any[]>([]);
  // Unstacked, range-filtered points on a real time axis; days with no engagements are left off.
  const visiblePoints = useMemo(() => visibleTrend(chartData, trendRange), [chartData, trendRange]);
  const trendXDomain = useMemo(() => trendDomain(trendRange), [trendRange]);
  const recordedDays = useMemo(() => recordedTrendDays(chartData), [chartData]);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isDemoData, setIsDemoData] = useState(false);

  // Handle view change
  const handleViewChange = (view: ViewType) => {
    setCurrentView(view);
    localStorage.setItem('portfolioViewMode', view);
  };

  // Fetch detailed grid data when Grid View is active
  useEffect(() => {
    if ((currentView === 'grid' || currentView === 'risk') && Object.keys(gridData).length === 0 && engagements.length > 0) {
      fetchGridData();
    }
  }, [currentView, engagements]);

  const fetchGridData = async () => {
    setLoadingGrid(true);
    const newGridData: Record<string, ClientGridData> = {};

    const fetchPromises = engagements.map(async (eng) => {
      try {
        const response = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/progress-grid/${eng.companyId}`,
          {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
          }
        );
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            const items = result.data;
            const statuses: Record<string, GridItemStatus> = {};
            
            DOMAINS.forEach((domain, index) => {
              const relevantItems = items.filter((_: any, i: number) => i % DOMAINS.length === index);
              let status: GridItemStatus['status'] = 'none';
              if (relevantItems.length === 0) status = 'none';
              else if (relevantItems.some((i: any) => i.isBlocked)) status = 'blocked';
              else if (relevantItems.some((i: any) => i.completion < 100 && i.completion > 0)) status = 'in-progress';
              else if (relevantItems.every((i: any) => i.completion === 100)) status = 'complete';
              else status = 'none';

              statuses[domain] = {
                domain,
                status,
                lastUpdated: new Date().toISOString()
              };
            });
            
            newGridData[eng.companyId] = {
              companyId: eng.companyId,
              statuses,
              blockedNames: items
                .filter((i: any) => i.isBlocked)
                .map((i: any) => String(i.name || i.title || i.label || 'Config item')),
            };
          }
        }
      } catch (e) {
        console.error(`Failed to load grid for ${eng.companyId}`, e);
      }
    });

    await Promise.all(fetchPromises);
    setGridData(newGridData);
    setLoadingGrid(false);
  };

  // LIVE SYNC & POLLING (Every 30s)
  useEffect(() => {
    let mounted = true;
    let pollInterval: any;

    const loadSnapshot = async () => {
      try {
        const snapRes = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/portfolio/health/trend`,
          {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
          }
        );
        if (snapRes.ok && mounted) {
          const data = await snapRes.json();
          if (data.trend) setChartData(data.trend);
          if (data.lastUpdated) setLastUpdated(data.lastUpdated);
          if (data.isDemoData !== undefined) setIsDemoData(data.isDemoData);
        }
      } catch (error) {
        console.error("Failed to load snapshot", error);
      }
    };

    const syncData = async (isPoll = false) => {
      if (isPoll && mounted) setIsRefreshing(true);
      try {
        const syncRes = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/portfolio/health/sync`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
          }
        );
        if (syncRes.ok && mounted) {
          const data = await syncRes.json();
          if (data.trend) setChartData(data.trend);
          if (data.lastUpdated) setLastUpdated(data.lastUpdated);
          if (data.isDemoData !== undefined) setIsDemoData(data.isDemoData);
        }
      } catch (error) {
        console.error("Failed to sync health data", error);
      } finally {
        if (isPoll && mounted) setIsRefreshing(false);
      }
    };

    if (accessToken) {
      // 1. Immediate Snapshot Load
      loadSnapshot().then(() => {
        // 2. Then Initial Sync (non-blocking)
        syncData(false);
      });

      // 3. Start Polling (30s)
      pollInterval = setInterval(() => {
        syncData(true);
      }, 30000);
    }

    return () => { 
      mounted = false; 
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [accessToken]);

  // Executive Summary Calculation
  const executiveSummary = useMemo(() => {
    if (engagements.length === 0) return null;
    
    const total = engagements.length;
    const onTrack = engagements.filter(e => e.healthStatus === 'healthy').length;
    const onTrackPercent = Math.round((onTrack / total) * 100);
    
    // Find largest risk exposure
    const atRiskEngagements = engagements.filter(e => e.healthStatus === 'at-risk');
    let largestRisk = null;
    if (atRiskEngagements.length > 0) {
      largestRisk = atRiskEngagements.sort((a, b) => b.totalItems - a.totalItems)[0];
    }
    
    return {
      percent: onTrackPercent,
      totalActive: total,
      largestRiskName: largestRisk?.companyName || "None"
    };
  }, [engagements]);

  // Format "Last updated" time
  const renderLastUpdated = () => {
    if (!lastUpdated) return <span className="text-xs text-muted-foreground">Last updated: —</span>;
    
    const dateObj = new Date(lastUpdated);
    const timeStr = dateObj.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    const dateStr = dateObj.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    
    const isToday = new Date().toDateString() === dateObj.toDateString();
    const dayLabel = isToday ? 'Today' : dateStr;
    
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Last updated: {dayLabel} (as of {timeStr})</span>
        {isRefreshing && (
          <span className="flex items-center text-[10px] text-primary animate-pulse">
             <RefreshCw className="size-3 mr-1 animate-spin" /> Refreshing...
          </span>
        )}
      </div>
    );
  };
  
  // Helper for Chart Date Labels
  // --- Views ---

  // 1. Grid View
  const renderGridView = () => {
    const filteredRows = engagements.filter(eng => {
      if (searchTerm && !eng.companyName.toLowerCase().includes(searchTerm.toLowerCase())) return false;
      if (showBlockedOnly && eng.blockedItems === 0) return false;
      if (showHighRisk && eng.healthStatus !== 'at-risk') return false;
      if (showClientStalled && eng.waitingForClient === 0) return false;
      return true;
    });

    return (
      <div className="space-y-4">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 p-2 bg-muted/30 rounded-lg">
          <div className="relative w-64">
             <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
             <Input 
               placeholder="Search clients..." 
               className="pl-8 h-8" 
               value={searchTerm}
               onChange={e => setSearchTerm(e.target.value)}
             />
          </div>
          <div className="h-6 w-px bg-border mx-2" />
          <Button 
            variant={showBlockedOnly ? "secondary" : "ghost"} 
            size="sm" 
            onClick={() => setShowBlockedOnly(!showBlockedOnly)}
            className={showBlockedOnly ? "bg-destructive/10 text-destructive hover:bg-destructive/15" : ""}
          >
            <AlertTriangle className="size-3 mr-1" />
            Blocked Only
          </Button>
          <Button 
             variant={showClientStalled ? "secondary" : "ghost"} 
             size="sm"
             onClick={() => setShowClientStalled(!showClientStalled)}
             className={showClientStalled ? "bg-warning/10 text-warning hover:bg-warning/15" : ""}
          >
            <Users className="size-3 mr-1" />
            Client Stalled
          </Button>
          <Button 
             variant={showHighRisk ? "secondary" : "ghost"} 
             size="sm"
             onClick={() => setShowHighRisk(!showHighRisk)}
             className={showHighRisk ? "bg-destructive/10 text-destructive hover:bg-destructive/15" : ""}
          >
            <AlertOctagon className="size-3 mr-1" />
            High Risk
          </Button>
        </div>

        {/* Heatmap */}
        <div className="border rounded-md overflow-x-auto bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="text-left p-3 font-medium min-w-[200px]">Client</th>
                {DOMAINS.map(domain => (
                  <th key={domain} className="p-3 font-medium text-center w-24">{domain}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredRows.map(eng => {
                const rowData = gridData[eng.companyId]?.statuses || {};
                
                return (
                  <tr key={eng.companyId} className="border-b last:border-0 hover:bg-muted/20 transition-colors group">
                    <td className="p-3 font-medium">
                      <button 
                        onClick={() => onViewClientDetails(eng.companyId)}
                        className="hover:text-primary hover:underline flex items-center gap-2 text-left"
                      >
                        {eng.companyName}
                        {eng.healthStatus === 'at-risk' && <Badge variant="destructive" className="h-5 px-1 text-[10px]">RISK</Badge>}
                      </button>
                    </td>
                    {DOMAINS.map((domain, i) => {
                      // No grid data for a cell means no data: a muted cell, never an invented color.
                      const cell = loadingGrid ? undefined : rowData[domain];
                      const status: GridItemStatus['status'] = cell?.status ?? 'none';
                      const style = CELL_STYLES[status];

                      return (
                        <td key={domain} className="p-1 text-center">
                          <TooltipProvider>
                            <Tooltip delayDuration={0}>
                              <TooltipTrigger asChild>
                                <div 
                                  className={`mx-auto size-6 rounded-sm cursor-pointer hover:opacity-80 transition-opacity ${style.color}`}
                                  onClick={() => onViewClientDetails(eng.companyId)}
                                />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="font-semibold">{domain}</p>
                                <p className="text-xs text-primary-foreground/75">{style.label}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filteredRows.length === 0 && (
            <div className="p-8 text-center text-muted-foreground">
              No clients match the current filters.
            </div>
          )}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 border-t bg-muted/30 text-xs text-muted-foreground">
            {(Object.keys(CELL_STYLES) as GridItemStatus['status'][]).map(state => (
              <span key={state} className="flex items-center gap-1.5">
                <span className={`size-3 rounded-sm ${CELL_STYLES[state].color}`} />
                {CELL_STYLES[state].label}
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // 2. Executive View (Treemap)
  const renderExecutiveView = () => {
    const data = engagements.map(eng => ({
      name: eng.companyName,
      size: eng.totalItems > 0 ? eng.totalItems : 50,
      health: eng.healthStatus,
      progress: eng.progressPercentage,
      blocked: eng.blockedItems,
      engagement: eng
    }));

    const CustomizedContent = (props: any) => {
      const { x, y, width, height, index } = props;
      const eng = data[index];
      if (!eng) return null;

      let fillColor = '#16a34a';
      const textColor = '#ffffff';
      
      if (eng.health === 'at-risk') {
        fillColor = '#ef4444';
      } else if (eng.health === 'needs-attention') {
        fillColor = '#d97706';
      }

      const safeName = props.name || '';
      const maxChars = Math.floor(width / 8); 
      const displayName = safeName.length > maxChars ? safeName.substring(0, maxChars) + '...' : safeName;
      
      const showDetails = width > 60 && height > 60;
      const showPercent = width > 40 && height > 40;

      return (
        <g 
          style={{ cursor: 'pointer' }}
          onClick={() => onViewClientDetails(eng.engagement.companyId)}
        >
          <rect
            x={x}
            y={y}
            width={width}
            height={height}
            rx={4} 
            ry={4}
            style={{
              fill: fillColor,
              stroke: '#fff',
              strokeWidth: 2,
              filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.1))',
              transition: 'all 0.2s ease',
            }}
            className="hover:opacity-90 hover:stroke-slate-200"
          />
          
          {showDetails && (
             <text 
               x={x + 6} 
               y={y + 20} 
               textAnchor="start" 
               fill={textColor} 
               fontSize={13} 
               fontWeight="bold"
               style={{ pointerEvents: 'none' }}
             >
               {displayName}
             </text>
          )}

          {showPercent && (
             <text 
               x={x + 6} 
               y={y + 38} 
               textAnchor="start" 
               fill="rgba(255,255,255,0.9)" 
               fontSize={16}
               fontWeight="300" 
               style={{ pointerEvents: 'none' }}
             >
               {eng.progress}%
             </text>
          )}
          
          {eng.blocked > 0 && width > 80 && (
             <text x={x + width - 20} y={y + 20} fontSize={11} fill="#d97706">!</text>
          )}
        </g>
      );
    };

    const CustomTooltip = ({ active, payload }: any) => {
      if (active && payload && payload.length) {
        const item = payload[0].payload;
        return (
          <div className="bg-popover text-popover-foreground p-3 border rounded shadow-lg text-sm max-w-[200px] z-50">
            <p className="font-bold mb-1">{item.name}</p>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Scope:</span>
                <span className="font-mono">{item.size} Items</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Progress:</span>
                <span className={`font-mono ${item.progress === 100 ? 'text-success font-bold' : ''}`}>{item.progress}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Risk:</span>
                <span className={`capitalize font-semibold ${
                    item.health === 'at-risk' ? 'text-destructive' : 
                    item.health === 'needs-attention' ? 'text-warning' : 'text-success'
                }`}>
                  {item.health.replace('-', ' ')}
                </span>
              </div>
              <div className="flex justify-between border-t pt-1 mt-1">
                <span className="text-muted-foreground">Last Updated:</span>
                <span>Today</span>
              </div>
              {item.blocked > 0 && (
                <div className="mt-2 text-destructive font-bold flex items-center gap-1">
                   <AlertTriangle className="size-3" />
                   {item.blocked} Blockers
                </div>
              )}
            </div>
          </div>
        );
      }
      return null;
    };

    return (
      <div className="h-[500px] w-full border rounded-md p-4 bg-white shadow-sm">
        <ResponsiveContainer width="100%" height="100%">
          <Treemap
            data={data}
            dataKey="size"
            aspectRatio={4 / 3}
            stroke="#fff"
            fill="#8884d8"
            content={<CustomizedContent />}
            isAnimationActive={false}
          >
            <RechartsTooltip content={<CustomTooltip />} cursor={false} />
          </Treemap>
        </ResponsiveContainer>
        
        <div className="flex justify-center gap-6 mt-4 text-sm border-t pt-3">
          <div className="flex items-center gap-2">
            <div className="size-3 bg-success rounded-sm shadow-sm" />
            <span className="text-muted-foreground">On Track</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="size-3 bg-warning rounded-sm shadow-sm" />
            <span className="text-muted-foreground">Needs Attention</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="size-3 bg-destructive rounded-sm shadow-sm" />
            <span className="text-muted-foreground">At Risk</span>
          </div>
        </div>
      </div>
    );
  };

  // 3. Risk Focus View
  const renderRiskView = () => {
    const atRiskClients = engagements
      .filter(e => e.healthStatus === 'at-risk' || e.blockedItems > 0)
      .sort((a, b) => b.blockedItems - a.blockedItems)
      .slice(0, 5);

    // Real blockers only: one row per client with blocked items, item names once the grid data has loaded.
    const blockedClients = engagements
      .filter(e => e.blockedItems > 0)
      .sort((a, b) => b.blockedItems - a.blockedItems);

    const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

    // The red card only exists when there is something critical to show.
    const criticalCard = atRiskClients.length > 0 ? (
      <Card className="h-full border-destructive/30 bg-destructive/5">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center text-destructive">
            <AlertTriangle className="size-5 mr-2" />
            Critical Attention Needed
          </CardTitle>
          <CardDescription>{plural(atRiskClients.length, 'client')} requiring immediate intervention</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {atRiskClients.map(client => (
              <div 
                key={client.companyId}
                className="bg-white p-3 rounded-md shadow-sm border border-destructive/20 cursor-pointer hover:border-destructive/50 transition-colors"
                onClick={() => onViewClientDetails(client.companyId)}
              >
                <div className="flex justify-between items-start mb-1">
                  <span className="font-semibold">{client.companyName}</span>
                  <Badge variant="destructive">{plural(client.blockedItems, 'Blocker')}</Badge>
                </div>
                <div className="text-xs text-muted-foreground">
                  Waiting on: {plural(client.waitingForConsultant, 'consultant action')}
                </div>
                <div className="mt-2 h-1.5 w-full bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-destructive" style={{ width: `${client.progressPercentage}%` }} />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    ) : null;

    return (
      <div className="space-y-4">
        {!criticalCard && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground px-1">
            <CheckCircle2 className="size-4 text-success" />
            No clients need intervention right now.
          </p>
        )}
        <div className={`grid grid-cols-1 gap-6 ${criticalCard ? 'md:grid-cols-3' : ''}`}>
          {criticalCard && <div className="md:col-span-1 space-y-4">{criticalCard}</div>}

          <div className={`${criticalCard ? 'md:col-span-2' : ''} space-y-4`}>
            <Card>
              <CardHeader className="pb-2 pt-4">
                 <CardTitle className="text-sm font-semibold flex items-center">
                   <XCircle className="size-4 mr-2 text-destructive" />
                   Top Blockers Across Portfolio
                 </CardTitle>
              </CardHeader>
              <CardContent className="pb-4">
                <div className="space-y-2">
                  {blockedClients.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No blocked items across the portfolio.</p>
                  ) : (
                    blockedClients.map(client => {
                      const names = gridData[client.companyId]?.blockedNames ?? [];
                      return (
                        <div 
                          key={client.companyId} 
                          className="flex items-center justify-between gap-3 text-sm p-2 bg-muted/40 rounded hover:bg-muted cursor-pointer"
                          onClick={() => onViewClientDetails(client.companyId)}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                             <Badge variant="outline" className="text-xs shrink-0">{client.companyName}</Badge>
                             <span className="truncate">
                               {plural(client.blockedItems, 'blocked item')}
                               {names.length > 0 && (
                                 <span className="text-muted-foreground">
                                   : {names.slice(0, 3).join(', ')}{names.length > 3 ? ` +${names.length - 3} more` : ''}
                                 </span>
                               )}
                             </span>
                          </div>
                          <Button variant="ghost" size="sm" className="h-6 text-xs text-primary shrink-0">Open client</Button>
                        </div>
                      );
                    })
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  };

  // 4. Trend View (Snapshot + Live)
  const renderTrendView = () => (
    <div className="bg-white border rounded-lg p-4 shadow-sm mb-6 animate-in fade-in slide-in-from-top-2 duration-500">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-3">
            <h3 className="font-semibold text-sm">Portfolio Health Trend</h3>
            {isDemoData && (
              <div className="flex items-center gap-1">
                <Badge variant="outline" className="text-[10px] h-5 px-1 bg-warning/10 text-warning border-warning/30">
                  Demo Data
                </Badge>
                <span className="text-[10px] text-muted-foreground">Real trend starts after first day of usage.</span>
              </div>
            )}
            {/* Last Updated Indicator */}
            {renderLastUpdated()}
          </div>
          {executiveSummary && (
            <p className="text-sm text-muted-foreground mt-1">
              Portfolio is <span className="font-medium text-foreground">{executiveSummary.percent}% On Track</span> across {executiveSummary.totalActive} active engagements. 
              {executiveSummary.largestRiskName !== 'None' && (
                <span className="ml-1">
                   Largest risk exposure: <span className="text-destructive font-medium">{executiveSummary.largestRiskName}</span>.
                </span>
              )}
            </p>
          )}
          {recordedDays < 5 && !isDemoData && recordedDays > 0 && (
             <p className="text-xs text-primary mt-1">
               Trend history is accumulating. {recordedDays} day{recordedDays > 1 ? 's' : ''} of data available.
             </p>
          )}
        </div>
        
        {/* Trend Range Selector */}
        <div className="flex items-center bg-muted/50 rounded-lg p-0.5 border">
          {(['30d', '90d', '6m'] as const).map(range => (
            <button
              key={range}
              onClick={() => setTrendRange(range)}
              className={`text-[10px] font-medium px-2.5 py-1 rounded-md transition-all ${
                trendRange === range 
                  ? 'bg-white text-primary shadow-sm' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {range.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <div className="h-[200px] w-full">
        {visiblePoints.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground text-sm bg-muted/10 rounded-lg border border-dashed">
            <Clock className="size-8 mb-2 opacity-20" />
            {recordedDays === 0 ? (
              <>
                <p>No historical data yet.</p>
                <p className="text-xs opacity-70">Trend will appear after the first day with active engagements.</p>
              </>
            ) : (
              <>
                <p>No health snapshots in this range.</p>
                <p className="text-xs opacity-70">Pick a longer range to see earlier days.</p>
              </>
            )}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={visiblePoints} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="colorOnTrack" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#16a34a" stopOpacity={0.1}/>
                  <stop offset="95%" stopColor="#16a34a" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorRisk" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ef4444" stopOpacity={0.1}/>
                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
              {/* A real time axis: a day without a snapshot is a gap, not the next neighbour. */}
              <XAxis 
                dataKey="ts" 
                type="number"
                scale="time"
                domain={trendXDomain}
                fontSize={10} 
                tickLine={false} 
                axisLine={false} 
                tickMargin={10} 
                minTickGap={30}
                tickFormatter={formatTrendTick}
              />
              <YAxis 
                fontSize={10} 
                tickLine={false} 
                axisLine={false} 
                tickFormatter={(val) => `${val}%`} 
                domain={[0, 100]}
              />
              <RechartsTooltip 
                contentStyle={{ borderRadius: '8px', fontSize: '12px', border: '1px solid #e2e8f0' }}
                itemStyle={{ padding: 0 }}
                labelFormatter={(ts) => formatTrendLabel(Number(ts))}
                formatter={(value) => `${value}%`}
              />
              {/* Each series is its own percentage of the portfolio: never stacked. */}
              <Area 
                type="linear" 
                dataKey="onTrack" 
                name="On Track" 
                stroke="#16a34a" 
                fill="url(#colorOnTrack)" 
                strokeWidth={2}
                dot={{ r: 3, strokeWidth: 0, fill: '#16a34a' }}
              />
              <Area 
                type="linear" 
                dataKey="needsAttention" 
                name="Needs Attention" 
                stroke="#d97706" 
                fill="none" 
                strokeWidth={2}
                strokeDasharray="4 4"
                dot={{ r: 3, strokeWidth: 0, fill: '#d97706' }}
              />
              <Area 
                type="linear" 
                dataKey="atRisk" 
                name="At Risk" 
                stroke="#ef4444" 
                fill="url(#colorRisk)" 
                strokeWidth={2}
                dot={{ r: 3, strokeWidth: 0, fill: '#ef4444' }}
              />
              <Legend iconType="circle" fontSize={10} wrapperStyle={{ fontSize: '10px', marginTop: '5px' }} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-4 mb-8 animate-in fade-in duration-500">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Portfolio Health</h2>
          <p className="text-sm text-muted-foreground">Monitor delivery risk and progress across all engagements</p>
        </div>
      </div>
      
      {/* Portfolio Trend Chart */}
      {renderTrendView()}

      {/* View Toggle */}
      <div className="flex justify-end w-full">
        <div className="flex flex-wrap sm:flex-nowrap items-center p-1 bg-muted/80 rounded-lg border border-border/40 shadow-inner w-full sm:w-auto">
          <button
            onClick={() => handleViewChange('grid')}
            className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary/20 ${
              currentView === 'grid' 
                ? 'bg-white text-primary shadow-sm font-bold border border-primary/20' 
                : 'text-muted-foreground font-medium hover:bg-muted hover:text-foreground border border-transparent'
            }`}
            aria-pressed={currentView === 'grid'}
          >
            <LayoutGrid className={`size-4 ${currentView === 'grid' ? 'text-primary' : 'opacity-70'}`} />
            Portfolio Grid
          </button>
          
          <button
            onClick={() => handleViewChange('executive')}
            className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary/20 ${
              currentView === 'executive' 
                ? 'bg-white text-primary shadow-sm font-bold border border-primary/20' 
                : 'text-muted-foreground font-medium hover:bg-muted hover:text-foreground border border-transparent'
            }`}
            aria-pressed={currentView === 'executive'}
          >
            <BarChart3 className={`size-4 ${currentView === 'executive' ? 'text-primary' : 'opacity-70'}`} />
            Executive
          </button>
          
          <button
            onClick={() => handleViewChange('risk')}
            className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 py-1.5 rounded-md text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary/20 ${
              currentView === 'risk' 
                ? 'bg-white text-primary shadow-sm font-bold border border-primary/20' 
                : 'text-muted-foreground font-medium hover:bg-muted hover:text-foreground border border-transparent'
            }`}
            aria-pressed={currentView === 'risk'}
          >
            <AlertTriangle className={`size-4 ${currentView === 'risk' ? 'text-primary' : 'opacity-70'}`} />
            Risk Focus
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="min-h-[300px]">
        {currentView === 'grid' && renderGridView()}
        {currentView === 'executive' && renderExecutiveView()}
        {currentView === 'risk' && renderRiskView()}
      </div>
    </div>
  );
}